import React from "react";

export interface AssetCardProps {
  id: number;
  model: string;
  brand: string;
  type: string;
  status: string;
  owner: string;
  location: string;
  identityType: string;
  identity: string;
  assign: string;
  assignDate: string;
  purchaseDate: string;
  assetType: string;
  config?: {
    ram?: number;
    processor?: string;
    harddisk?: number;
    harddiskType?: string;
    operatingSystem?: string;
  };
  onView?: (id: number) => void;
}

const AssetCard: React.FC<AssetCardProps> = ({

  model,
  brand,
  type,
  status,
  owner,
  location,
  identityType,
  identity,
  assign,
  assignDate,
  purchaseDate,
  assetType,

}) => {
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    if (date.getFullYear() <= 1970) return "N/A";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes("running") || statusLower.includes("active")) {
      return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700";
    } else if (
      statusLower.includes("not working") ||
      statusLower.includes("damaged")
    ) {
      return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700";
    } else if (statusLower.includes("maintenance")) {
      return "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700";
    }
    return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
  };

  const getTypeIcon = (type: string) => {
    const typeLower = type.toLowerCase();
    if (typeLower.includes("mobile") || typeLower.includes("phone")) {
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zm3 14a1 1 0 100-2 1 1 0 000 2z" />
        </svg>
      );
    } else if (typeLower.includes("laptop") || typeLower.includes("computer")) {
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z"
            clipRule="evenodd"
          />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
          clipRule="evenodd"
        />
      </svg>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow duration-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 dark:from-gray-700 dark:to-gray-900 p-4 text-white">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {getTypeIcon(type)}
            <span className="text-xs font-medium uppercase tracking-wide">
              {type}
            </span>
          </div>
          <span
            className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(status)}`}
          >
            {status}
          </span>
        </div>
        <h3 className="text-lg font-bold truncate" title={model}>
          {model}
        </h3>
        <p className="text-blue-100 text-sm">{brand}</p>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Assigned To */}
        <div className="flex items-start gap-2">
          <svg
            className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 dark:text-gray-400">Assigned To</p>
            <p
              className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate"
              title={assign}
            >
              {assign || "Unassigned"}
            </p>
          </div>
        </div>

        {/* Owner */}
        <div className="flex items-start gap-2">
          <svg
            className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 dark:text-gray-400">Owner</p>
            <p
              className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate"
              title={owner}
            >
              {owner || "N/A"}
            </p>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-start gap-2">
          <svg
            className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 dark:text-gray-400">Location</p>
            <p
              className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate"
              title={location}
            >
              {location || "N/A"}
            </p>
          </div>
        </div>

        {/* Identity */}
        <div className="flex items-start gap-2">
          <svg
            className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {identityType || "Identity"}
            </p>
            <p
              className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate"
              title={identity}
            >
              {identity || "N/A"}
            </p>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Purchase Date</p>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {formatDate(purchaseDate)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Assign Date</p>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {formatDate(assignDate)}
            </p>
          </div>
        </div>

        {/* Asset Type Badge */}
        <div className="pt-2">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">
            {assetType} Asset
          </span>
        </div>
      </div>

    </div>
  );
};

export default AssetCard;
